// [목적] DayPlan CRUD mutation hooks — POST/PATCH/DELETE days 를 react-query useMutation 으로 래핑
// [이유] WC-FE-FOLIO-02 — FolioEditPage 에서 Day 추가·수정·삭제 시 BE 호출 + folio 캐시 invalidate
// [사이드 이팩트] onSettled 에서 queryKeys.folio(folioId) invalidate → FolioPage/FolioEditPage 자동 갱신
// [수정 시 고려사항] create/update 낙관적 업데이트는 미적용 (Day 구조 변경은 BE 재계산 의존). 필요 시 후속 Spec 에서 추가

import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { CreateDayInput, DayPlan, UpdateDayInput } from '@gangwoncanvas/types';

import { createDayPlan, deleteDayPlan, patchDayPlan } from '../api/folioClient.js';
import { queryKeys } from '../api/queryKeys.js';

// ─── 타입 ────────────────────────────────────────────────────────────────────

interface CreateDayArgs {
  folioId: string;
  input: CreateDayInput;
}

interface UpdateDayArgs {
  folioId: string;
  dayId: string;
  input: UpdateDayInput;
}

interface DeleteDayArgs {
  folioId: string;
  dayId: string;
}

// ─── 훅 ──────────────────────────────────────────────────────────────────────

/**
 * [목적] DayPlan 생성 mutation — POST /folios/:id/days
 * [이유] WC-FE-FOLIO-02 — Day 추가 UI 에서 호출. 성공 후 folio 캐시 invalidate 로 UI 갱신
 * [사이드 이팩트] onSettled 에서 queryKeys.folio(folioId) invalidate
 * [수정 시 고려사항] mutation result 의 data 로 새 DayPlan id/dayIndex 를 얻어 UI 자동 선택 가능
 */
export function useCreateDay() {
  const qc = useQueryClient();

  return useMutation<DayPlan, Error, CreateDayArgs>({
    mutationFn: ({ folioId, input }) => createDayPlan(folioId, input),
    onSettled: (_data, _err, { folioId }) => {
      void qc.invalidateQueries({ queryKey: queryKeys.folio(folioId) });
    },
  });
}

/**
 * [목적] DayPlan 수정 mutation — PATCH /folios/:id/days/:dayId
 * [이유] WC-FE-FOLIO-02 — Day 요약·날짜 편집 시 호출
 * [사이드 이팩트] onSettled 에서 queryKeys.folio(folioId) invalidate
 * [수정 시 고려사항] 낙관적 업데이트 없음 — totalCostEstimate 재계산은 BE 응답에 의존
 */
export function useUpdateDay() {
  const qc = useQueryClient();

  return useMutation<DayPlan, Error, UpdateDayArgs>({
    mutationFn: ({ folioId, dayId, input }) => patchDayPlan(folioId, dayId, input),
    onSettled: (_data, _err, { folioId }) => {
      void qc.invalidateQueries({ queryKey: queryKeys.folio(folioId) });
    },
  });
}

/**
 * [목적] DayPlan 삭제 mutation — DELETE /folios/:id/days/:dayId
 * [이유] WC-FE-FOLIO-02 — Day 삭제 버튼 클릭 시 호출. CASCADE 로 하위 PlanItem 전부 삭제
 * [사이드 이팩트] onSettled 에서 queryKeys.folio(folioId) invalidate. 삭제 후 activeDay 인덱스 조정은 컴포넌트 책임
 * [수정 시 고려사항] 삭제 확인 다이얼로그는 컴포넌트 레벨 책임. 낙관적 업데이트 시 dayIndex 재정렬 복잡 — 미적용
 */
export function useDeleteDay() {
  const qc = useQueryClient();

  return useMutation<void, Error, DeleteDayArgs>({
    mutationFn: ({ folioId, dayId }) => deleteDayPlan(folioId, dayId),
    onSettled: (_data, _err, { folioId }) => {
      void qc.invalidateQueries({ queryKey: queryKeys.folio(folioId) });
    },
  });
}
